<script setup>
// Display hints and a crossword grid, dynamically generated based on word lengths.
import { computed } from 'vue';
const props = defineProps({
  words: Array,
  showAnswers: Boolean,
  showHints: Boolean,
  totalTime: Number,
  timeLeft: Number,
});

/* Words is an array of objects. Objects look like this:

{
  vertical: bool -- true is vertical, false is horiz
  position: { x: number, y: number} -- Starting position for the word
  word: string -- the word itself
  hint: string -- the clue displayed on the screen to help players solve the word
}

*/
const gridLength = computed(() => {
  let maxPos = 0;
  for (let a = 0; a < props.words.length; a += 1) {
    const w = props.words[a];
    const pos = !w.vertical ? w.position.x : w.position.x + w.word.length -1;
    console.log(pos, w);
    if (pos > maxPos) maxPos = pos;
  }
  return maxPos;
});
const gridHeight = computed(() => {
  let maxPos = 0;
  for (let a = 0; a < props.words.length; a += 1) {
    const w = props.words[a];
    const pos = w.vertical ? w.position.y: w.position.y + w.word.length - 1;
    if (pos > maxPos) maxPos = pos;
  }
  return maxPos;
});

const showHints = computed(() => {
  if (props.timeLeft < (props.totalTime - 30)) {
    return true;
  }
  return false;
})


const wordsWithPositions = computed(() => {
  // Output array of words. Each entry is an array of objects (letters) with x/y coordinates
  const words = [];
  const startingGridPoints = []; // So we can check for duplicate starting points
  for (let a = 0; a < props.words.length; a += 1) {
    const word = [];
    const w = props.words[a];
    for (let b = 0; b < w.word.length; b += 1) {
      const gridPoint = {
        x: !w.vertical ? w.position.x : w.position.x + b,
        y: w.vertical ? w.position.y : w.position.y + b,
        letter: w.word[b],
        display: false,
        key: a,
        first: false, // First letter in sequence, so we can show key.
        intersects: false, // Two clues that start on the same tile. Put the shared key here
      }
      if (b === 0) gridPoint.first = true;
      const startingPoint = `${gridPoint.x} ${gridPoint.y}`;
      // Deal with two potential clues starting on same square.

      if (startingGridPoints.length && startingGridPoints.filter(x => x === startingPoint).length) {
        try {
          gridPoint.intersects = words.filter(w => `${w[0].x} ${w[0].y}` === startingPoint)[0][0].key;
        } catch {}
      } 
      startingGridPoints.push(startingPoint);
      word.push(gridPoint);
    }
    words.push(word);
  }
  for (let i = 0; i < words.length; i += 1) {
  for (let j = 0; j < words[i].length; j += 1) {
    for (let k = i + 1; k < words.length; k += 1) {
      for (let l = 0; l < words[k].length; l += 1) {
        if (words[i][j].x === words[k][l].x && words[i][j].y === words[k][l].y) {
          words[i][j].intersects = true;
          words[k][l].intersects = true;
        }
      }
    }
  }
}
  return words;
});

function squareContent(x, y) {
  const words = wordsWithPositions.value; // Array of Arrays of objects. Each object has the gridpoint object above
  let result;
  for (let a = 0; a < words.length; a += 1) {
    const w = words[a].filter((word) => word.x === x && word.y === y);
    for (let b = 0; b < w.length; b += 1) {
      result = {
        letter: w[0].letter.toUpperCase(),
        display: w[0].display,
        intersects: false,
        first: w[0].first,
        key: w[0].key + 1,
      };
      if (w[0].intersects || w[0].intersects === 0) {
        const keys = `${Number(w[0].intersects + 1)} ${Number(w[0].key + 1)}`;
        result.key = keys;
      }
      if (w[b].intersects) {
        result.intersects = true;
      }
    }
  }
  return result;
}

function squareStyleClass(x, y) {
  const sq = squareContent(x, y);
  let returnClass = '';
  if (sq && sq.letter) {
    returnClass = 'crossword__content';
  }
  if (sq && sq.display) {
    returnClass += ' crossword__square__display';
  }
  return returnClass;
}

function squareStyleHide() {
  return 'crossword__square__hide';
}

function displayHint(obj) {
  return `(${obj.word.length}) ${obj.hint}`;
}

const listOfIndexes = computed(() => {
  const indexes = [];
  for (let a = 0; a < wordsWithPositions.value.length; a += 1) {
    let index = Number(a) + 1;
    // if (wordsWithPositions.value[a][0].intersects) {
    //   index = wordsWithPositions.value[a][0].letter;
    // }
    indexes.push(index);
  }
  return indexes;
});

</script>

<template>
  <div class="crossword__container">
    <!-- Display the grid -->
    <div
      class="crossword__td__left">
      <table
          class="crossword__puzzle">
        <tbody>
            <tr
                v-for="x in gridLength"
                :key="x"
                class="crossword__row">
                <td
                    v-for="y in gridHeight"
                    :key="y"
                    class="crossword__square"
                    :class="squareStyleClass(x, y)">
                  <span
                    v-if="squareContent(x, y) && squareContent(x, y).first && !squareContent(x, y).intersects"
                    :class="props.words[squareContent(x, y).key - 1].vertical
                      ? 'crossword__key__offset__down'
                      : 'crossword__key__offset__across'">
                    {{squareContent(x, y).key}}
                    <!-- {{props.words[squareContent(x, y).key - 1].vertical ? '↓' : '→'}} -->
                  </span>
                  <span
                  v-if="squareContent(x, y) && squareContent(x, y).first && squareContent(x, y).intersects"
                  :class="props.words[squareContent(x, y).key.split(' ')[0] - 1].vertical
                      ? 'crossword__key__offset__down'
                      : 'crossword__key__offset__across'">
                      {{squareContent(x, y).key.split(' ')[1]}}
                      <!-- {{props.words[squareContent(x, y).key.split(' ')[1] - 1].vertical ? '↓' : '→'}} -->
                  </span>
                <!-- <span
                    v-if="squareContent(x, y) && squareContent(x, y).key > 0 && squareContent(x, y).key <= props.words.length && squareContent(x, y).first"
                    :class="props.words[squareContent(x, y).key - 1].vertical ? 'crossword__key__offset__down' : 'crossword__key__offset__across'"
                    v-text="`${squareContent(x, y).key}${props.words[squareContent(x, y).key - 1].vertical ? '↓' : '→'}`"
                /> -->
                <Transition
                    name="fade"
                    mode="out-in">
                  <span
                      v-if="squareContent(x, y) && showAnswers"
                      v-text="squareContent(x, y).letter"
                  />
                  <span
                      v-else-if="squareContent(x, y) && props.showHints && squareContent(x, y).first"
                      v-text="squareContent(x, y).letter"
                  />
                  <!-- <span
                      v-else-if="squareContent(x, y) && squareContent(x, y).intersects && squareContent(x, y).first"
                      :class="props.words[squareContent(x, y).key.split(' ')[0] - 1].vertical ? 'crossword__key__offset__down' : 'crossword__key__offset__across'"
                      v-text="`${squareContent(x, y).letter}${ ? '↓' : '→'}`"
                  /> -->
                  <span
                      v-else-if="squareContent(x, y) && squareContent(x, y).intersects"
                      v-text="squareContent(x, y).letter"
                  />
                  <span
                  v-else-if="squareContent(x, y)"
                  :class="squareStyleHide()">
                </span>
              </Transition>
              </td>
            </tr>
        </tbody>
      </table>
    </div>
    <!-- Hints for each of the words -->
    <div
        class="crossword__td">
      <div
          v-for="(word, key) in words"
          class="crossword__hint__container">
        <div
            class="crossword__hint__key"
            v-text="`${listOfIndexes[key]} ${word.vertical ? '↓' : '→'}`"
        />
        <div
            v-if="word && word.hint"
            class="crossword__hint"
            v-text="displayHint(word)"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.crossword {
  &__td {
    margin: 20px;
    flex: 1;
    &__left {
      @extend .crossword__td;
      margin-left: 85px;
    }
  }
  &__container {
    display: flex;
    margin-top: 100px;
  }
  &__puzzle {
    border-radius: 5px;
    width: 100%;
  }
  &__key {
    border-radius: 10px 30px 30px 10px;
    font-size: .5em;
    display: inline-block;
    flex: 0.15;
    background-color: gold;
    color: darkslategrey;
    margin: auto;
    border: 10px solid skyblue;
    &__offset {
      border: 5px solid skyblue;
      position: absolute;
      font-weight: 800;
      // border-radius: 30px 30px 30px 30px;
      @extend .crossword__key;
      &__center {
        @extend .crossword__key__offset;
        top: 0px;
        left: 0px;
        border-radius: 30px 30px 30px 30px;
        padding: 5px 20px 5px 20px;
      }
      &__across {
        @extend .crossword__key__offset;
        border-radius: 10px 50px 50px 10px;
        right: 100px;
        top: 0px;
        line-height: 1.5em;
        padding: 3px 25px 3px 10px;
      }
      &__down {
        padding: 3px 10px 10px 10px;
        @extend .crossword__key__offset;
        border-radius: 10px 10px 50px 50px;
        left: 20px;
        bottom: 90px; }
      &__above {
        padding: 3px 3px 20px 3px;
        @extend .crossword__key__offset;
        border-radius: 10px 10px 50px 50px;
        top: 140px; }
    }
  }
  &__hint {
    flex: 1;
    &__container {
      color: white;
      border: 2px solid gold;
      display: flex;
      font-size: 1em;
      border-radius: 10px;
      margin: 10px 10px;
      padding: 25px 0.5vw;
    }
  }
  &__square {
    // border: 1px solid white;
    &__hide {
      color: white !important;
    }
  }
  &__content {
    width: 120px;
    height: 100px;
    // background: linear-gradient(to bottom right, #ffd700, #ffcc00, #ffdb58, #ffe58f, #fff1b8);
    font-family: 'Courier';
    text-shadow: 2px 2px 4px white;
    background-color: white;
    color: darkslategrey;
    font-size: 2.15em;
    line-height: 0.85em;
    margin: auto;
    border: 4px solid skyblue;
    font-weight: 800;
  }
}

.fade-enter-active {
  transition: all 1.75s ease-out;
}

.fade-leave-active {
  transition: all .25s ease-out;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

</style>
